import React, {
  FunctionComponent,
  useState,
  useMemo,
  useCallback,
  useEffect,
} from 'react';

import {
  AsyncState,
  InstitutionAdminSettingsState,
  PendingInvitation,
} from 'src/ReduxStorage/InstitutionAdminSettings/types';
import { PortalResourceGetPaginatedPendingInvitations } from 'src/resource/PortalResource/types';
import moment from 'moment';
import { debounce } from 'lodash';
import { Spinner } from 'src/web/components/Spinner/Spinner';
import { CursorPaginate } from 'src/web/components/Pagination/CursorPaginate';
import { ActionTable } from '../../../../../components/ActionTable/component';
import { Key, Row, SortBy } from '../../../../../components/ActionTable/types';
import {
  NameEmailInfo,
  UserEmail,
  PaginationContainer,
  FilterWrapper,
  StyledTextField,
  ActionTableWrapper,
  SpinnerWrapper,
} from '../styles';
import profileImage from '../images/profileImage.svg';

export interface PendingTeamMemberTableProps {
  paginatedPendingInvitations: InstitutionAdminSettingsState['paginatedPendingInvitations'];
  currentInstitutionPath: string;
  getPaginatedPendingInvitations: (
    params: PortalResourceGetPaginatedPendingInvitations['args']
  ) => Promise<unknown>;
}

export const PendingTeamMemberTable: FunctionComponent<PendingTeamMemberTableProps> = ({
  paginatedPendingInvitations,
  getPaginatedPendingInvitations,
}) => {
  const [selectedRows, setSelectedRows] = useState<Key[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [sortBy, setSortBy] = useState<SortBy>();
  const [searchUserQuery, setSearchUserQuery] = useState('');
  // Pagination states
  const [pageSize, setPageSize] = useState(10);
  const [nextItemStack, setNextItemStack] = useState<PendingInvitation[]>([]);

  const [
    pendingInvitationsQueryParams,
    setPendingInvitationsQueryParams,
  ] = useState<PortalResourceGetPaginatedPendingInvitations['args']>({
    limit: pageSize,
  });

  const debouncedGetPaginatedPendingInvitations = useCallback(
    debounce((args: PortalResourceGetPaginatedPendingInvitations['args']) => {
      getPaginatedPendingInvitations(args);
    }, 300),
    []
  );

  useEffect(() => {
    debouncedGetPaginatedPendingInvitations(pendingInvitationsQueryParams);
  }, [pendingInvitationsQueryParams]);

  const {
    asyncState,
    total,
    invitedUsers,
    hasNextPage,
  } = paginatedPendingInvitations;

  const resetPagination = useCallback((): void => setNextItemStack([]), [
    setNextItemStack,
  ]);

  useEffect(() => {
    let sortingParams: { sortBy?: string; sortDirection?: -1 | 1 } = {};
    if (!sortBy) {
      sortingParams = {
        sortBy: undefined,
        sortDirection: undefined,
      };
    } else {
      sortingParams = {
        sortBy: sortBy.sortKey,
        sortDirection: sortBy.direction === 'ascending' ? 1 : -1,
      };
    }
    setPendingInvitationsQueryParams((prev) => ({
      ...prev,
      ...sortingParams,
      afterItem: undefined,
    }));
    resetPagination();
  }, [sortBy]);

  const onSearchByUserValueChange = (value: string): void => {
    setSearchUserQuery(value);
    setPendingInvitationsQueryParams((prev) => ({
      ...prev,
      filterByUser: value,
      afterItem: undefined,
    }));
    resetPagination();
  };

  const onNextPageCursorPaginate = (): void => {
    const lastItem = invitedUsers[invitedUsers.length - 1];
    setPendingInvitationsQueryParams((prev) => ({
      ...prev,
      afterItem: lastItem,
    }));
    // Push latest nextItem to the stack to keep track nextItems on previous pages
    setNextItemStack((prev) => [...prev, lastItem]);
  };

  const onPrevPageCursorPaginate = (): void => {
    // Get the previous page's nextItem
    const secondLastItem = nextItemStack[nextItemStack.length - 2];
    setPendingInvitationsQueryParams((prev) => ({
      ...prev,
      afterItem: secondLastItem,
    }));
    setNextItemStack((prev) => prev.slice(0, prev.length - 1));
  };

  const onPageSizeChange = (newPageSize: number): void => {
    setPageSize(newPageSize);
    setPendingInvitationsQueryParams((prev) => ({
      ...prev,
      afterItem: undefined,
      limit: newPageSize,
    }));
    resetPagination();
  };

  const pendingMemberRows = useMemo((): Row[] => {
    const ret: Row[] = [];
    for (let i = 0; i < invitedUsers.length; i++) {
      ret.push({
        key: invitedUsers[i].id,
        cells: [
          {
            cell: (
              <NameEmailInfo>
                <img alt="user-profile" src={profileImage} />
                <div>
                  <b>{invitedUsers[i].fullName}</b>
                  <UserEmail>{invitedUsers[i].email}</UserEmail>
                </div>
              </NameEmailInfo>
            ),
          },
          // {
          //   // @TODO: replace this to using user's data
          //   cell: 'Administrator',
          // },
          {
            cell: moment(invitedUsers[i].createdOn).format(
              'DD MMMM YYYY - hh:mm a Z'
            ),
          },
        ],
      });
    }
    return ret;
  }, [invitedUsers]);

  return (
    <>
      <FilterWrapper>
        <StyledTextField
          label="Search by name or email"
          placeholder="Start typing..."
          value={searchUserQuery}
          onValueChange={onSearchByUserValueChange}
        />
      </FilterWrapper>
      <ActionTableWrapper>
        {asyncState === AsyncState.LOADING && (
          <SpinnerWrapper>
            <Spinner />
          </SpinnerWrapper>
        )}
        <ActionTable
          fixTopHeaders
          fixLeftHeaders
          itemName="learner"
          headers={[
            { key: 'name_email', cell: 'Name / Email' },
            // { key: 'institution_role', cell: 'Institution role' },
            {
              key: 'createdOn',
              cell: 'Date invited',
              sortKey: 'createdOn',
            },
          ]}
          rows={pendingMemberRows}
          toolbarActions={<div>Button</div>}
          selectedRows={selectedRows}
          selectAll={selectAll}
          totalItems={pendingMemberRows.length}
          sortBy={sortBy}
          onSelectedRowsChanged={setSelectedRows}
          onSelectAllChanged={setSelectAll}
          onSortChanged={setSortBy}
        />
      </ActionTableWrapper>
      <PaginationContainer>
        <CursorPaginate
          pageSize={pageSize}
          totalResults={total}
          showPageSizeSelector
          onNextPage={onNextPageCursorPaginate}
          onPreviousPage={onPrevPageCursorPaginate}
          handlePageSizeChange={onPageSizeChange}
          hasNextPage={hasNextPage}
          hasPreviousPage={nextItemStack.length > 0}
        />
      </PaginationContainer>
    </>
  );
};
